'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from 'react';
import { useDownloadEmVoo } from '@/shared/hooks/useDownloadEmVoo';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { baixarPdfCertificadoAcessosSentenca, fetchCertificadoAcessosSentenca } from '../store/certificado-sentenca/actions';
import { selectCertificadoAcessosSentencaDoAlvo, selectCertificadoAcessosSentencaError, selectCertificadoAcessosSentencaLoading, selectCertificadoAcessosSentencaNaoPublicada, selectCertificadoAcessosSentencaPdfBaixando, selectCertificadoAcessosSentencaSemPermissao } from '../store/certificado-sentenca/selectors';
import type { OrigemDocumentoEncerramento } from '../types/encerramento.types';

/**
 * O certificado é único por processo — secretaria, árbitro, advogado, parte e notificado leem
 * exatamente a mesma peça, com a situação de TODAS as partes (rn8/rn9). A `origem` só escolhe o
 * endereço do papel (cada porta exige a sessão daquele papel).
 *
 * Recarrega a cada montagem, e é de propósito: a TELA não é congelada (rn11) — uma entrega
 * registrada depois tem de aparecer na abertura seguinte. O PDF é a certidão emitida no servidor,
 * com código próprio; o download em voo é abortado ao sair, como no certificado da citação, para o
 * arquivo de quem já saiu não ir para o disco.
 */
export function useCertificadoAcessosSentenca(processoId, origem) {
  const $ = _c(32);
  const dispatch = useAppDispatch();
  const {
    showError,
    showSuccess
  } = useToast();
  let t0;
  if ($[0] !== processoId) {
    t0 = state => selectCertificadoAcessosSentencaDoAlvo(state, processoId);
    $[0] = processoId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const certificado = useAppSelector(t0);
  const isLoading = useAppSelector(selectCertificadoAcessosSentencaLoading);
  const error = useAppSelector(selectCertificadoAcessosSentencaError);
  const semPermissao = useAppSelector(selectCertificadoAcessosSentencaSemPermissao);
  const naoPublicada = useAppSelector(selectCertificadoAcessosSentencaNaoPublicada);
  const isDownloadingPdf = useAppSelector(selectCertificadoAcessosSentencaPdfBaixando);
  const {
    baixar,
    abortar
  } = useDownloadEmVoo(baixarPdfCertificadoAcessosSentenca.rejected.match);
  let t1;
  if ($[2] !== dispatch || $[3] !== origem || $[4] !== processoId) {
    t1 = () => {
      dispatch(fetchCertificadoAcessosSentenca({
        origem,
        id: processoId
      }));
    };
    $[2] = dispatch;
    $[3] = origem;
    $[4] = processoId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const recarregar = t1;
  let t2;
  let t3;
  if ($[6] !== recarregar) {
    t2 = () => {
      recarregar();
    };
    t3 = [recarregar];
    $[6] = recarregar;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== abortar) {
    t4 = () => abortar;
    $[9] = abortar;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== abortar || $[12] !== origem || $[13] !== processoId) {
    t5 = [abortar, origem, processoId];
    $[11] = abortar;
    $[12] = origem;
    $[13] = processoId;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== baixar || $[16] !== certificado || $[17] !== dispatch || $[18] !== origem || $[19] !== processoId || $[20] !== showError || $[21] !== showSuccess) {
    t6 = async () => {
      if (!certificado) {
        return;
      }
      const desfecho = await baixar(() => dispatch(baixarPdfCertificadoAcessosSentenca({
        origem,
        id: processoId,
        nomeArquivo: `certificado-acessos-sentenca-${certificado.numeroProcesso.toLowerCase()}.pdf`
      })));
      if (desfecho.tipo === "ignorado") {
        return;
      }
      if (desfecho.tipo === "falhou") {
        showError("Erro ao gerar PDF", desfecho.erro ?? "Falha ao gerar o PDF");
        return;
      }
      showSuccess("PDF gerado com sucesso");
    };
    $[15] = baixar;
    $[16] = certificado;
    $[17] = dispatch;
    $[18] = origem;
    $[19] = processoId;
    $[20] = showError;
    $[21] = showSuccess;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const baixarPdf = t6;
  const imprimir = _temp;
  let t7;
  if ($[23] !== baixarPdf || $[24] !== certificado || $[25] !== error || $[26] !== isDownloadingPdf || $[27] !== isLoading || $[28] !== naoPublicada || $[29] !== recarregar || $[30] !== semPermissao) {
    t7 = {
      certificado,
      isLoading,
      error,
      semPermissao,
      naoPublicada,
      recarregar,
      isDownloadingPdf,
      baixarPdf,
      imprimir
    };
    $[23] = baixarPdf;
    $[24] = certificado;
    $[25] = error;
    $[26] = isDownloadingPdf;
    $[27] = isLoading;
    $[28] = naoPublicada;
    $[29] = recarregar;
    $[30] = semPermissao;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
}
function _temp() {
  if (typeof window === "undefined") {
    return;
  }
  window.print();
}